import type { ReactNode } from 'react'
import { createContext, use } from 'react'
import { Select as BaseSelect } from '@base-ui/react/select'

import { ChevronDownIcon } from '@gto/icons-react/outline/chevron-down'
import { cn } from '@gto/ui/cn'
import { withPopupOpenMarker } from '@gto/ui/state-markers'

import { selectVariants } from './select.styles'

type SelectSize = 'small' | 'medium'

export type SelectOption<T extends string = string> = {
  value: T
  label: ReactNode
  disabled?: boolean
  /** Styles the rendered item — e.g. a rule setting one option apart from the rest. */
  className?: string
  'data-tst'?: string
}

// `options` reaches `Select.List` through context rather than a prop so the trigger and
// the list cannot be given different lists — see the `Root` docblock.
const OptionsContext = createContext<readonly SelectOption[] | undefined>(undefined)

type RootProps<T extends string> = Omit<
  BaseSelect.Root.Props<T, false>,
  'items' | 'onValueChange'
> & {
  options?: readonly SelectOption<T>[]
  onValueChange?: (value: T) => void
}

/**
 * Pass `options` and the select is complete: it resolves the selected label for
 * `Select.Value` AND renders the items, so an empty `<Select.List />` is all the dropdown
 * needs.
 *
 * That single prop is deliberate. Base UI takes the label source (`items`) and the item
 * elements separately and types the former optional, so omitting it type-checks and ships a
 * trigger reading `FULLY_ANALYZED` instead of "Fully analyzed". One prop for both makes that
 * state unreachable.
 *
 * Give `Select.List` children instead when an item needs more than `SelectOption` carries
 * (a wrapper element, custom markup); `options` still resolves the trigger label.
 */
const Root = <T extends string>({ options, onValueChange, children, ...props }: RootProps<T>) => (
  <OptionsContext value={options}>
    <BaseSelect.Root
      items={options}
      onValueChange={(value) => {
        // Base UI types a cleared selection as `null`; no DS select offers a clear affordance.
        if (value !== null) {
          onValueChange?.(value)
        }
      }}
      {...props}
    >
      {children}
    </BaseSelect.Root>
  </OptionsContext>
)

type TriggerProps = Omit<BaseSelect.Trigger.Props, 'style' | 'size'> & {
  size?: SelectSize
}

const Trigger = ({ className, size, children, ...props }: TriggerProps) => {
  const { trigger } = selectVariants({ size })

  return (
    <BaseSelect.Trigger
      className={withPopupOpenMarker(trigger(), className)}
      {...props}
    >
      {children}
    </BaseSelect.Trigger>
  )
}

type ValueProps = BaseSelect.Value.Props

const Value = ({ className, children, ...props }: ValueProps) => {
  const { value } = selectVariants()

  return (
    <BaseSelect.Value
      className={cn(value(), className)}
      {...props}
    >
      {children}
    </BaseSelect.Value>
  )
}

const Icon = ({ className, children, ...props }: BaseSelect.Icon.Props) => {
  const { icon } = selectVariants()

  return (
    <BaseSelect.Icon
      className={cn(icon(), className)}
      {...props}
    >
      {children ?? <ChevronDownIcon />}
    </BaseSelect.Icon>
  )
}

type DropdownProps = Omit<BaseSelect.Positioner.Props, 'style'> & {
  className?: string
  /**
   * `anchor` (the default) pins the popup to the trigger's width. `content` lets it grow past
   * a narrow trigger so a long option stays on one line, never shrinking below the trigger.
   */
  width?: 'anchor' | 'content'
}

const Dropdown = ({ className, width, children, ...props }: DropdownProps) => {
  const { positioner, popup } = selectVariants({ width })

  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        alignItemWithTrigger={false}
        className={positioner()}
        sideOffset={4}
        {...props}
      >
        {/* `className` styles the popup surface; remaining props configure the positioner */}
        <BaseSelect.Popup className={cn(popup(), className)}>{children}</BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  )
}

/** Renders `Root`'s `options` unless given children. */
const List = ({ className, children, ...props }: BaseSelect.List.Props) => {
  const { list } = selectVariants()
  const options = use(OptionsContext)

  return (
    <BaseSelect.List
      className={cn(list(), className)}
      {...props}
    >
      {children ??
        options?.map(
          ({ value, label, disabled, className: itemClassName, 'data-tst': dataTst }) => (
            <Item
              key={value}
              className={itemClassName}
              data-tst={dataTst}
              disabled={disabled}
              value={value}
            >
              {label}
            </Item>
          ),
        )}
    </BaseSelect.List>
  )
}

const Item = ({ className, children, ...props }: BaseSelect.Item.Props) => {
  const { item } = selectVariants()

  return (
    <BaseSelect.Item
      className={cn(item(), className)}
      {...props}
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  )
}

const Group = ({ className, ...props }: BaseSelect.Group.Props) => {
  const { group } = selectVariants()

  return (
    <BaseSelect.Group
      className={cn(group(), className)}
      {...props}
    />
  )
}

const GroupLabel = ({ className, ...props }: BaseSelect.GroupLabel.Props) => {
  const { groupLabel } = selectVariants()

  return (
    <BaseSelect.GroupLabel
      className={cn(groupLabel(), className)}
      {...props}
    />
  )
}

export const Select = Object.assign(Root, {
  Trigger,
  Value,
  Icon,
  Dropdown,
  List,
  Item,
  Group,
  GroupLabel,
  Separator: BaseSelect.Separator,
  ScrollUpArrow: BaseSelect.ScrollUpArrow,
  ScrollDownArrow: BaseSelect.ScrollDownArrow,
  Backdrop: BaseSelect.Backdrop,
})

export type { TriggerProps as SelectTriggerProps }
